import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeEnCA from '@angular/common/locales/en-CA';
import localeFrCA from '@angular/common/locales/fr-CA';
import {
  type ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, TitleStrategy } from '@angular/router';
import { authConfig } from '@core/config/auth.config';
import { httpAuthInterceptor } from '@core/interceptors/auth.interceptor';
import { httpErrorInterceptor } from '@core/interceptors/error.interceptor';
import { httpApiUrlInterceptor } from '@core/interceptors/url.interceptor';
import { LOCALE_PROVIDER } from '@core/providers/locale.provider';
import { BracedMissingTranslationHandler } from '@core/providers/ngx-translate/missing-translation.handler';
import { NamespaceTranslateLoader } from '@core/providers/ngx-translate/translate.loader';
import { TranslatedTitleStrategy } from '@core/providers/ngx-translate/translated-title.strategy';
import { provideMmscanCharts } from '@mmscan/angular-charts';
import { provideMmscanEditor } from '@mmscan/angular-editor';
import { provideMmscanPdfViewer } from '@mmscan/angular-pdf-viewer';
import { MmscanThemeService, provideMmscanUi } from '@mmscan/angular-ui/core';
import { mmscanLoadingInterceptor } from '@mmscan/angular-ui/loading';
import {
  provideMissingTranslationHandler,
  provideTranslateLoader,
  provideTranslateService,
  TranslateService,
} from '@ngx-translate/core';
import { provideAuth } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { routes } from './routes/app.routes';

// The app is a single bundle: CLDR data for both supported locales must be
// registered explicitly (per-locale builds used to provide it).
registerLocaleData(localeEnCA);
registerLocaleData(localeFrCA);

const userLocale = getCookie('locale') || 'EN_CA';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    // Library strings come from the `ui` translation namespace (public/i18n/*/ui.json).
    provideMmscanUi({
      i18n: () => toSignal(inject(TranslateService).stream('ui'), { initialValue: {} }),
    }),
    provideMmscanEditor({
      i18n: () => toSignal(inject(TranslateService).stream('ui.editor'), { initialValue: {} }),
    }),
    provideMmscanCharts({
      i18n: () => toSignal(inject(TranslateService).stream('ui.charts'), { initialValue: {} }),
    }),
    provideMmscanPdfViewer({
      workerSrc: '/pdf.worker.min.mjs',
      i18n: () => toSignal(inject(TranslateService).stream('ui.pdfViewer'), { initialValue: {} }),
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([httpApiUrlInterceptor, mmscanLoadingInterceptor, httpErrorInterceptor, httpAuthInterceptor])
    ),
    provideAuth(authConfig),
    provideTranslateService({
      loader: provideTranslateLoader(NamespaceTranslateLoader),
      missingTranslationHandler: provideMissingTranslationHandler(BracedMissingTranslationHandler),
      fallbackLang: 'en-CA',
    }),
    // Blocks first render until translations are loaded, so translate.instant()
    // is safe everywhere from app start.
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const initialLang = userLocale === 'FR_CA' ? 'fr-CA' : 'en-CA';
      return firstValueFrom(translate.use(initialLang)).catch(() => undefined);
    }),
    // Restore the saved light/dark choice before first render (MmscanThemeService only reads the
    // `.mmscan-dark` class; it does not apply the stored preference on its own).
    provideAppInitializer(() => {
      const theme = inject(MmscanThemeService);
      theme.setTheme(theme.savedTheme() ?? 'light');
    }),
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    LOCALE_PROVIDER,
  ],
};
